/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { Dropdown, IDropdown, IDropdownOption, IDropdownProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { ISearchBoxProps, ISearchBoxStyles } from '@fluentui/react/lib/SearchBox';
import { ISelectableOption } from '@fluentui/react/lib/SelectableOption';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { IRenderFunction, classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { noopUndefined } from 'src/core/utils/base';
import { globalMessages } from 'src/core/utils/globalMessages';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';

export type LuisDropdownProps = Omit<IDropdownProps, 'onChange'> & {
    /**
     * A flag to indicate whether the dropdown
     * is in a loading state or not.
     *
     * @default false
     */
    isLoading?: boolean;

    /**
     * Support auto select dropdown item on open. If
     * this flag is false, a hidden placeholder item
     * is added to the dropdown to inhibit it from
     * auto selecting an option on change.
     *
     * @default true
     */
    autoSelect?: boolean;

    /**
     * If this flag is true, adds a "No items" option
     * when there are no options passed to the dropdown.
     *
     * @default false
     */
    enableEmptyState?: boolean;

    /**
     * An optional placeholder to display when the dropdown
     * is in an empty state. This parameter is ignored
     * if the `enableEmptyState` is false and search is not enabled.
     */
    emptyStatePlaceholder?: string;

    /**
     * Order the items by this optional key
     * it's default value is text.
     */
    orderByKey?: keyof IDropdownOption | 'none';

    /**
     * Determines whether the drop down has searching
     * capabilities or not.
     *
     * @default true
     */
    isSearchEnabled?: boolean;

    /**
     * Optional placeholder for the search box displayed
     * if isSearchEnabled is true.
     */
    searchBoxPlaceholder?: string;

    /**
     * Optional styles for the wrapper that contains the list
     * items under the search box in the search mode.
     */
    searchListWrapperStyles?: IStackStyles;

    /**
     * Optional size of the displayed options in the start.
     * This size also represents the size of any chunk will be added to
     * the currently displayed options after clicking on Load more.
     *
     * @default 100
     */
    optionsChunkSize?: number;

    maxHeight?: number;

    /**
     * Dropdown action items to be rendered in the tail of the dropdown.
     */
    onRenderTailActionItems?: (query: string) => IDropdownOption[];

    /**
     * Optional onRender method to override the no search
     * match item found rendering.
     */
    onRenderNoMatchItem?: (query: string) => IDropdownOption;

    /**
     * Optional callback fired when an option
     * is selected from the dropdown.
     */
    onChange?: (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number, currentQuery?: string) => void;

    /**
     * Optional callback to allow the handling of query change from a higher order
     * component
     */
    handleQueryChange?: (newQuery: string) => void;

    /**
     * Determines whether there is select all option or not in multiSelect mode.
     *
     * @default false
     */
    isSelectAllEnabled?: boolean;

    /**
     * Determines whether select all option is selected or not in the beginning.
     *
     * @default false
     */
    isSelectAllSelected?: boolean;

    /**
     * Optional callback fired with all filtered items when selectAll item is changed from the dropdown.
     */
    onSelectAllItems?: (isSelectAllSelected: boolean, options: IDropdownOption[]) => void;

    /**
     * Determines whether the dropdown options are ready or not.
     */
    areOptionsLoading?: boolean;

    /**
     * the user displayed role.
     */
    displayedUserRole?: string;
};

const messages = defineMessages({
    defaultNoItemsLabel: {
        id: 'core.luisDropdown.defaultNoItemsLabel',
        defaultMessage: 'No items found'
    },
    searchBoxPlaceholder: {
        id: 'core.luisDropdown.searchBoxPlaceholder',
        defaultMessage: 'Search'
    },
    loadMore: {
        id: 'core.luisDropdown.loadMore',
        defaultMessage: 'Load more ...'
    },
    expandedDropDownState: {
        id: 'core.luisDropdown.expandedDropDownState',
        defaultMessage: 'expanded'
    },
    selectAllOption: {
        id: 'core.luisDropdown.selectAllOption',
        defaultMessage: 'Select all'
    },
    prebuiltDropDownAriaLabel: {
        id: 'clu.cluEntityPrebuiltCapabilityTable.prebuiltDropDownAriaLabel',
        defaultMessage: 'Prebuilts dropdown'
    }
});

const dropdownTheme = getScopedTheme('LuisDropdown');
const dropdownStyles = classNamesFunction<IDropdownProps, IDropdownStyles>()({
    callout: { minWidth: '200px' }
});
const loadMoreButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { color: dropdownTheme.blueColor } });
const searchBoxStyles = classNamesFunction<ISearchBoxProps, ISearchBoxStyles>()({
    root: {
        border: 0,
        zIndex: 1,
        width: '100%',
        position: 'fixed',
        borderBottom: `1px solid ${dropdownTheme.searchBoxBorderColor}`,
        '&:hover': {
            borderBottom: `1px solid ${dropdownTheme.searchBoxBorderColor}`
        }
    }
});

const optionsListStackStyles = (maxHeight: number) =>
    classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
        root: { maxHeight, overflowY: 'auto' }
    });

const optionsListStackStylesWithSearchEnabled = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { marginTop: '35px !important' }
});

export const LuisDropdown = (props: LuisDropdownProps) => {
    const {
        options,
        onChange,
        disabled,
        onDismiss,
        multiSelect,
        maxHeight = 300,
        handleQueryChange,
        isLoading = false,
        autoSelect = true,
        displayedUserRole,
        orderByKey = 'text',
        onRenderNoMatchItem,
        searchBoxPlaceholder,
        emptyStatePlaceholder,
        optionsChunkSize = 100,
        isSearchEnabled = true,
        searchListWrapperStyles,
        onRenderTailActionItems,
        enableEmptyState = false,
        areOptionsLoading = false,
        isSelectAllEnabled = false,
        isSelectAllSelected = false,
        selectedKey,
        onSelectAllItems = noopUndefined,
        ...rest
    } = props;

    React.useEffect(() => {
        if (props.ariaLabel === localize(messages.prebuiltDropDownAriaLabel)) {
            compRef.current.focus();
        }
    }, [selectedKey]);
    const { localize } = useLocalization();

    const compRef = React.useRef<IDropdown>();
    const dropDownRef = React.useRef<HTMLDivElement>();
    const noItemsKey = React.useRef(getId('noItems'));
    const itemWrapperRef = React.useRef<HTMLDivElement>(null);
    const hiddenItemKey = React.useRef(getId('hiddenItem'));
    let optionsToRender: IDropdownOption[] = options ? options.slice() : [];

    const [query, setQuery] = React.useState('');
    const [isDropDownExpanded, setIsDropDownExpanded] = React.useState<boolean>(false);
    const [displayedOptionsCount, setDisplayedOptionsCount] = React.useState(optionsChunkSize);
    let filteredItemsCount = 0;

    const getEmptyItem = () =>
        query && onRenderNoMatchItem
            ? onRenderNoMatchItem(query)
            : {
                  key: noItemsKey.current,
                  text: emptyStatePlaceholder ?? localize(messages.defaultNoItemsLabel),
                  disabled: true
              };

    /**
     * If the enable empty state flag is true, show the no
     * items placeholder when there are no options.
     */
    if (enableEmptyState && optionsToRender.length === 0) {
        optionsToRender.unshift(getEmptyItem());
    }

    if (orderByKey !== 'none') {
        optionsToRender = orderBy(optionsToRender, [createKeyedStringNormalizer(orderByKey)], 'asc').map((item, index) => ({
            ...item,
            index
        }));
    }

    if (isSearchEnabled && !isLoading) {
        optionsToRender = optionsToRender.filter(option => !query || option.text.toLocaleLowerCase().includes(query.toLocaleLowerCase()));

        const activeItemsCount = optionsToRender.filter(o => o.key !== hiddenItemKey.current && !o.hidden).length;

        filteredItemsCount = activeItemsCount;
        if (!activeItemsCount) {
            optionsToRender = [getEmptyItem()];
        }
    }

    if (isSelectAllEnabled && multiSelect && optionsToRender.length) {
        optionsToRender.unshift({
            key: 'selectAll',
            text: localize(messages.selectAllOption)
        });
    }
    /**
     * If the dropdown has the auto select flag as turned
     * off, then a hidden item is added to disallow the
     * dropdown from auto selecting an element when it
     * opens or is focused.
     *
     * @see https://github.com/OfficeDev/office-ui-fabric-react/issues/5473
     */
    if (!autoSelect) {
        optionsToRender.unshift({ key: hiddenItemKey.current, text: '', hidden: true });
    }

    React.useEffect(() => {
        if (isSelectAllEnabled && multiSelect && isSelectAllSelected) {
            onChange(null, optionsToRender[0], 0, '');
            onSelectAllItems(true, optionsToRender);
        }
    }, [isSelectAllEnabled, multiSelect, isSelectAllSelected]);

    /**
     * If the change event is triggered with the hidden placeholder,
     * the event is swallowed to abstract it away from the consumer.
     * Otherwise the event is fired normally.
     */
    const onChangeInterceptor = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number): void => {
        if (option.key === hiddenItemKey.current) {
            return;
        }

        if (isSearchEnabled && !isSelectAllEnabled) {
            setQuery('');
        }

        onChange(event, option, index, query);

        if (option.key === 'selectAll' && index === 0 && isSelectAllEnabled) {
            onSelectAllItems(option.selected, optionsToRender);
        }
    };

    /**
     * Renders a loading spinner if the option is of
     * loading type.
     */
    const onRenderPlaceholder: IRenderFunction<IDropdownProps> = (dropdownProps: IDropdownProps, defaultRender) => {
        if (isLoading) {
            return (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                    <Spinner size={SpinnerSize.small} />
                    <LuisText>{localize(globalMessages.loading)}</LuisText>
                </Stack>
            );
        }

        return defaultRender(dropdownProps);
    };

    /**
     * Auto-scrolls the given dropdown list to the first
     * item starting with the given key code.
     */
    const onCallOutKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (isSearchEnabled) {
            return;
        }

        const itemToScrollTo = optionsToRender.find(option =>
            option.text.toLocaleLowerCase().startsWith(String.fromCharCode(event.keyCode).toLocaleLowerCase())
        );
        const elementToScroll = document.querySelector(`#item${itemToScrollTo.index}`);

        elementToScroll.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        if (props.calloutProps?.onKeyDown) {
            props.calloutProps.onKeyDown(event);
        }
    };

    const onDismissInterceptor = () => {
        if (isSearchEnabled) {
            setQuery('');
        }

        onDismiss?.();
        setIsDropDownExpanded(false);

        const dropDownElement = dropDownRef.current?.getElementsByClassName('ms-Dropdown')?.[0] as HTMLElement;
        dropDownElement?.focus();
    };

    const onRenderItem = (itemProps: ISelectableOption, defaultRenderer: (props?: ISelectableOption) => JSX.Element) => (
        <div ref={itemWrapperRef} id={`item${itemProps.index}`} key={`item${itemProps.index}`}>
            {defaultRenderer(itemProps)}
        </div>
    );

    const onRenderTitle = (titleProps: IDropdownOption<any>[], defaultRenderer: (props?: IDropdownOption<any>[]) => JSX.Element) => (
        <>
            {isLoading ? (
                <Stack horizontal verticalAlign="center">
                    <Spinner size={SpinnerSize.small} styles={{ root: { marginRight: 10 } }} />
                    {defaultRenderer(titleProps)}
                </Stack>
            ) : (
                <>{defaultRenderer(titleProps)}</>
            )}
        </>
    );

    const onRenderOption = (option: ISelectableOption) => {
        const secondaryText = (option.data as { secondaryText: string })?.secondaryText;
        const isActionItem = (option.data as { isActionItem: boolean })?.isActionItem;

        const title = secondaryText ? `${option.text} ${secondaryText}` : option.text;

        return (
            <RBACTooltip
                displayedUserRole={displayedUserRole}
                styles={{ root: { maxWidth: '100%' } }}
                hidden={!isActionItem || !option.disabled}
                data-automation-id="luisDropDownActionItemLanguageToolTip"
                componentSurroundedByToolTip={
                    <TruncatedText
                        title={title}
                        isDisabled={option.disabled}
                        color={isActionItem && dropdownTheme.actionItemColor}
                        maxWidth={itemWrapperRef.current?.offsetWidth - 16 || '100%'}
                        styles={mergeStyleSets(
                            { root: { display: 'inline-block' } },
                            option.disabled && { root: { color: dropdownTheme.disabledItemColor } }
                        )}>
                        {option.text}
                        {secondaryText && (
                            <LuisText variant="xSmall" color={dropdownTheme.secondaryTextColor} styles={{ root: { marginLeft: 5 } }}>
                                {secondaryText}
                            </LuisText>
                        )}
                    </TruncatedText>
                }
            />
        );
    };

    const optionsList = (listProps: IDropdownProps, defaultRenderer: (props?: IDropdownProps) => JSX.Element) => {
        if (areOptionsLoading) {
            return <Spinner size={SpinnerSize.medium} styles={{ root: { padding: 5 } }} />;
        }
        return (
            <Stack
                styles={mergeStyleSets(
                    optionsListStackStyles(maxHeight),
                    searchListWrapperStyles,
                    isSearchEnabled && optionsListStackStylesWithSearchEnabled
                )}>
                {defaultRenderer({ ...listProps, options: listProps.options.slice(0, displayedOptionsCount) })}

                {displayedOptionsCount < optionsToRender.length && (
                    <ActionButton
                        aria-label={localize(messages.loadMore)}
                        data-automation-id="loadMore"
                        styles={loadMoreButtonStyles}
                        onClick={() => setDisplayedOptionsCount(displayedOptionsCount + optionsChunkSize)}>
                        {localize(messages.loadMore)}
                    </ActionButton>
                )}
            </Stack>
        );
    };

    const onRenderList = (listProps: IDropdownProps, defaultRenderer: (props?: IDropdownProps) => JSX.Element) => {
        if (!isSearchEnabled) {
            return optionsList(listProps, defaultRenderer);
        }

        return (
            <Stack>
                <SearchResultsAnnouncer isActive={query.length > 0} itemsLength={filteredItemsCount} />

                <LuisDebouncedSearchBox
                    autoFocus
                    value={query}
                    styles={searchBoxStyles}
                    onChange={(_, newValue?: string) => {
                        setQuery(newValue);
                        handleQueryChange?.(newValue);
                    }}
                    placeholder={searchBoxPlaceholder ?? localize(messages.searchBoxPlaceholder)}
                    debounceTime={300}
                    onClear={() => {
                        setQuery('');
                        handleQueryChange?.('');
                    }}
                />

                {optionsList(listProps, defaultRenderer)}
            </Stack>
        );
    };

    const getExpandedDropDownAriaLabel = () => {
        if (isDropDownExpanded) {
            return {
                ariaLabel: `${props.label ?? props.ariaLabel ?? ''} ${localize(messages.expandedDropDownState)}`
            };
        }

        return {};
    };

    const renderableItems = React.useMemo(
        () => (onRenderTailActionItems && isDropDownExpanded ? [...optionsToRender, ...onRenderTailActionItems(query)] : optionsToRender),
        [isDropDownExpanded, onRenderTailActionItems, optionsToRender]
    );

    return (
        <Dropdown
            ref={dropDownRef}
            multiSelect={multiSelect}
            options={renderableItems}
            onRenderList={onRenderList}
            onRenderItem={onRenderItem}
            onRenderTitle={onRenderTitle}
            onChange={onChangeInterceptor}
            componentRef={compRef}
            onRenderOption={onRenderOption}
            onDismiss={onDismissInterceptor}
            disabled={disabled || isLoading}
            {...getExpandedDropDownAriaLabel()}
            selectedKey={selectedKey}
            onRenderPlaceholder={onRenderPlaceholder}
            {...rest}
            styles={mergeStyleSets(dropdownStyles, props.styles)}
            calloutProps={{ ...rest.calloutProps, onKeyDown: onCallOutKeyDown, styles: { root: { overflow: 'unset' } } }}
            onClick={e => {
                props.onClick?.(e);
                setIsDropDownExpanded(!isDropDownExpanded);
            }}
            onKeyDown={e => {
                props.onKeyDown?.(e);
                if ((e.key === LuisKeyCodes.Enter || e.key === LuisKeyCodes.Space) && !isDropDownExpanded) {
                    setIsDropDownExpanded(true);
                }
            }}
        />
    );
};
